import { computed } from "vue";
import { useApiVersion } from "./useApiVersion";

const { apiVersion } = useApiVersion();

/**
 * Note: the modbus addresses in public/data/params-*.csv are accompanied by
 * "in code" names (in column 10). However, PCS was adamant that the modbus
 * addresses are EXTREMELY unlikely to be changed; at the same time they
 * could not give any such guarantee for the "in code" names. Therefore,
 * it was decided to use hardcoded addresses.
 */
export function useModbusAddress() {
  // -------- old ------------------------------------------------------------
  const modbusAddressSetOld: Record<string | symbol, number> = Object.freeze({
    softwareVersion: 0,
    hardwareVersion: 2,
    parameterVersion: 4,
    serialNumber: 6,
    serialNumberPostfix: 8,

    actualSetpoint: 22,
    setpointManagerMin: 24,
    setpointManagerMax: 26,
    setpointWanted: 34,

    inputVoltage: 44,
    deviceTemperature: 50,
    relativeHumidity: 52,

    referencePressure: 100,
    bodyTemperature: 102,
    referencePressureUserStart: 112,
    referencePressureUserEnd: 114,
    referencePressureUnit: 128,

    sensorAMin: 159,
    sensorAMax: 161,
    sensorAProcessType: 184,
    sensorAUnit: 199,

    sensorBMin: 209,
    sensorBMax: 211,
    sensorBProcessType: 234,
    sensorBUnit: 249,

    sensorCMin: 259,
    sensorCMax: 261,
    sensorCProcessType: 284,
    sensorCUnit: 299,

    identification: 800,

    valveInDuty: 936,
    valveOutDuty: 938,

    ledOnTime: 990,
    ledOffTime: 992,
    ledOnRed: 994,
    ledOnGreen: 995,
    ledOnBlue: 996,
    ledOffRed: 997,
    ledOffGreen: 998,
    ledOffBlue: 999,

    logDate: 5006,
    logDay0DateTime: 5006,
    logDay1DateTime: 5008,
    logDay2DateTime: 5010,
    logDay3DateTime: 5012,
    logDay4DateTime: 5014,
    logDay5DateTime: 5016,
    logDay6DateTime: 5018,

    accessLevel: 6000,
  });

  // -------- new ------------------------------------------------------------
  const modbusAddressSetNew: Record<string | symbol, number> = Object.freeze({
    softwareVersion: 0,
    hardwareVersion: 2,
    parameterVersion: 4,
    serialNumber: 6,
    serialNumberPostfix: 8,

    actualSetpoint: 1007,
    setpointManagerMin: 1009,
    setpointManagerMax: 1011,
    setpointWanted: 1019,

    inputVoltage: 302,
    deviceTemperature: 350,
    deviceTemperatureName: 2600,

    relativeHumidity: 352,

    bodyTemperature: 3400,
    referencePressure: 3402,
    referencePressureUserStart: 3412,
    referencePressureUserEnd: 3414,
    referencePressureUnit: 3421,
    referenceSensorName: 2200,

    sensorAMin: 3009,
    sensorAMax: 3011,
    sensorAProcessType: 3034,
    sensorAUnit: 3047,
    sensorAName: 2300,

    sensorBMin: 3109,
    sensorBMax: 3111,
    sensorBProcessType: 3134,
    sensorBUnit: 3147,
    sensorBName: 2400,

    sensorCMin: 3209,
    sensorCMax: 3211,
    sensorCProcessType: 3234,
    sensorCUnit: 3247,
    sensorCName: 2500,

    identification: 2000,

    valveInDuty: 3606,
    valveOutDuty: 3608,

    ledOnTime: 50,
    ledOffTime: 52,
    ledOnRed: 54,
    ledOnGreen: 55,
    ledOnBlue: 56,
    ledOffRed: 57,
    ledOffGreen: 58,
    ledOffBlue: 59,

    logDate: 60006,
    logDay0DateTime: 60006,
    logDay1DateTime: 60008,
    logDay2DateTime: 60010,
    logDay3DateTime: 60012,
    logDay4DateTime: 60014,
    logDay5DateTime: 60016,
    logDay6DateTime: 60018,

    accessLevel: 50000,
  });

  const isLegacyVersion = computed<boolean>(() => {
    return (
      apiVersion.value.substring(0, 2) === "0." ||
      apiVersion.value.substring(0, 2) === "1."
    );
  });

  const currentAddressSet = computed<Record<string | symbol, number>>(() =>
    isLegacyVersion.value ? modbusAddressSetOld : modbusAddressSetNew,
  );

  const modbusAddress = new Proxy({} as typeof modbusAddressSetOld, {
    get(target, prop) {
      return currentAddressSet.value[prop];
    },
  });

  return { modbusAddress };
}
